import React, { useState } from "react";
import styles from "./PlaydateInviteRecipientView.module.css";
import { DayPart, PlaydateInvite } from "../../../gql/types";
import PlaydateInviteBaseView from "./PlaydateInviteBaseView";
import ActionButton from "../../button/ActionButton";
import UnderlineButton from "../../button/UnderlineButton";
import PlaydateInviteFullDetails from "../PlaydateInviteFullDetails";
import downArrowIcon from '../../../assets/icons/downArrow.svg';
import upArrowIcon from '../../../assets/icons/upArrow.svg';
import TimeSelectionSelectionHeader from "../../time/TimeSelectionSelectionHeader";
import TimeSelectionSelectionView from "../../time/TimeSelectionSelectionView";
import { convertScheduleOptionsToProposedDates } from "../../../service/playdate/converters/converter";
import { DayPartSelection, TimeSelectionRow } from "../../time/types";
import DayPartRow from "../../time/DayPartRow";
import PlaydateInviteResponseList from "../PlaydateInviteResponseList";
import { renderCollapsibleSection } from "./PlaydateInviteOrganizerInPlanningView";

type Props = {
    invite: PlaydateInvite;
    onBack: () => void;
    onConfirm: (selection: DayPartSelection | null) => void;
    loading?: boolean;
};

function isTimeSelectionRowArray(arr: any[]): arr is TimeSelectionRow[] {
    return arr.length > 0 && "startTime" in arr[0] && "endTime" in arr[0];
}

type SelectionSource = "poll" | "manual" | null;

const PlaydateInviteFinalizeDecisionView: React.FC<Props> = ({ invite, onBack, onConfirm, loading }) => {
    const [isGuestListOpen, setIsGuestListOpen] = useState(false);
    const [pollSelection, setPollSelection] = useState<DayPartSelection[]>([]);
    const [manualDate, setManualDate] = useState<string>("");
    const [manualDayPart, setManualDayPart] = useState<DayPart | null>(null);
    const [activeSource, setActiveSource] = useState<SelectionSource>(null);

    const handlePollSelection = (selection: DayPartSelection[] | TimeSelectionRow[]) => {
        if (isTimeSelectionRowArray(selection)) {
            return;
        }
        setPollSelection(selection);
        setManualDayPart(null);
        setActiveSource(selection.length > 0 ? "poll" : null);
    };

    const manualSelection: DayPartSelection[] =
        manualDate && manualDayPart
            ? [{ selectedDate: manualDate, selectedDayPart: manualDayPart }]
            : [];

    const finalSelection: DayPartSelection | null =
        activeSource === "poll"
            ? pollSelection[0] ?? null
            : activeSource === "manual"
                ? manualSelection[0] ?? null
                : null;

    return (
        <PlaydateInviteBaseView
            onBack={onBack}
            title="Playdate Details"
            infoText="Select the final date and time for your event. Plii will update your guests!"
            topIcon="happy"
            useSectionDivider={true}
        >
            {/* Replace below with the guest responses */}
            {renderCollapsibleSection({
                title: "Guests:",
                isOpen: isGuestListOpen,
                onToggle: () => setIsGuestListOpen(prev => !prev),
                content: <PlaydateInviteResponseList inviteResponses={invite.responses ?? []} />,
            })}


            <div className={styles.sectionHeader}>
                <TimeSelectionSelectionHeader
                    header={"Final Date & Time:"}
                />
            </div>
            <TimeSelectionSelectionView
                mode={"daypart"}
                proposedDates={convertScheduleOptionsToProposedDates(invite.scheduleOptions)}
                onSelectionChange={handlePollSelection}
                groupByDate={true}
                singleSelection={true}
                readOnly={activeSource === "manual"}
            />
            <div className={styles.sectionDivider} />
            <div className={styles.sectionHeader}>
                Or Choose a New Final Date & Time:
            </div>
            <div className={styles.dayPartRow}>
                <DayPartRow
                    id={0}
                    date={manualDate}
                    selectedDayParts={manualDayPart !== null ? [manualDayPart] : []}
                    onRowChange={(id, field, value) => {
                        if (field === "selectedDayParts" && Array.isArray(value)) {
                            if (value.length === 0) {
                                setActiveSource(null);
                                setManualDayPart(null);
                                return;
                            }
                            setActiveSource("manual");
                            setManualDayPart(value[0] ?? null);
                        } else if (field === "date" && typeof value === "string") {
                            if (manualDayPart !== null) {
                                setActiveSource("manual");
                            }
                            setManualDate(value);
                        }
                    }}
                    singleSelection={true}
                    enableDateSelection={true}
                    noPastDate={true}
                    readOnly={activeSource === "poll"}
                />
            </div>
            <div className={styles.sectionDivider} />

            <div className={styles.buttonContainer}>
                <ActionButton
                    onClick={() => {
                        if (finalSelection) {
                            onConfirm(finalSelection);
                        }
                    }}
                    isLongText
                    disabled={!finalSelection}
                    hasLoadingSignal={loading}
                >
                    Confirm this Invite
                </ActionButton>
                <UnderlineButton onClick={onBack}>
                    Back
                </UnderlineButton>
            </div>
        </PlaydateInviteBaseView>
    );
};

export default PlaydateInviteFinalizeDecisionView;
